import * as PIXI from 'pixi.js';
import { COLORS, DIMENSIONS } from '../utils/constants';
import { logger } from '../utils/logger';

export class PixiApplicationManager {
  private app: PIXI.Application | null = null;
  private viewport: PIXI.Container | null = null;
  private grid: PIXI.Graphics | null = null;

  async initialize(container: HTMLDivElement): Promise<{
    app: PIXI.Application;
    viewport: PIXI.Container;
    grid: PIXI.Graphics;
  }> {
    try {
      // Cleanup existing app
      this.destroy();

      // Create Pixi Application
      this.app = new PIXI.Application();
      
      // Initialize with proper error handling. The renderer fills the canvas
      // container, which the flex layout sizes between the side panels; a
      // ResizeObserver keeps it in sync as panels open, close or resize.
      await this.app.init({
        width: container?.clientWidth || window.innerWidth,
        height: container?.clientHeight || window.innerHeight - DIMENSIONS.HEADER_HEIGHT,
        backgroundColor: COLORS.BACKGROUND,
        antialias: true,
        autoDensity: true,
        resolution: window.devicePixelRatio || 1,
        preference: 'webgl',
        powerPreference: 'low-power',
        autoStart: false, // Prevent automatic ticker start
      });

      // Validate that the app was initialized properly
      if (!this.app || !this.app.ticker || !this.app.stage || !this.app.renderer) {
        throw new Error('Pixi.js application failed to initialize properly');
      }

      // Validate container
      if (!container) {
        throw new Error('Container element is required for Pixi.js initialization');
      }

      // Add canvas to DOM
      try {
        container.innerHTML = '';
        const canvas = this.app.canvas as HTMLCanvasElement;
        if (!canvas) {
          throw new Error('Canvas element not created by Pixi.js');
        }
        container.appendChild(canvas);
      } catch (error) {
        throw new Error(`Failed to add canvas to container: ${error}`);
      }

      // Create viewport container
      this.viewport = new PIXI.Container();
      this.viewport.x = 0;
      this.viewport.y = 0;
      this.viewport.scale.x = 1;
      this.viewport.scale.y = 1;

      // Create grid graphics
      this.grid = new PIXI.Graphics();
      
      // Add to stage with validation
      if (!this.app.stage) {
        throw new Error('Pixi.js stage not available');
      }
      
      this.app.stage.addChild(this.grid);
      this.app.stage.addChild(this.viewport);

      logger.pixi.info('Pixi.js Application initialized successfully', {
        renderer: this.app.renderer?.type || 'unknown',
        canvas: !!this.app.canvas,
        ticker: !!this.app.ticker,
        stage: !!this.app.stage
      });

      return {
        app: this.app,
        viewport: this.viewport,
        grid: this.grid
      };
    } catch (error) {
      // Cleanup on error
      this.destroy();
      throw new Error(`Pixi.js initialization failed: ${error}`);
    }
  }

  destroy(): void {
    try {
      if (this.app) {
        // Stop ticker before destroying
        if (this.app.ticker) {
          this.app.ticker.stop();
        }
        
        // Clear stage before destroying app
        if (this.app.stage) {
          this.app.stage.removeChildren();
        }
        
        // Ensure we have a proper PIXI Application with destroy method
        if (typeof this.app.destroy === 'function') {
          this.app.destroy(true, true);
        } else {
          logger.pixi.warn('app.destroy is not a function, app may not be properly initialized');
        }
        this.app = null;
      }
      
      this.viewport = null;
      this.grid = null;
    } catch (error) {
      logger.pixi.warn('Error during PixiApplicationManager cleanup', error);
      // Force null even on error
      this.app = null;
      this.viewport = null;
      this.grid = null;
    }
  }

  /** Resizes the renderer to the given canvas-container dimensions. */
  resize(width: number, height: number): void {
    try {
      if (this.app && this.app.renderer) {
        this.app.renderer.resize(width, height);
      }
    } catch (error) {
      logger.pixi.warn('Error during resize', error);
    }
  }

  getApp(): PIXI.Application | null {
    return this.app;
  }

  getViewport(): PIXI.Container | null {
    return this.viewport;
  }

  getGrid(): PIXI.Graphics | null {
    return this.grid;
  }

  isInitialized(): boolean {
    return !!(this.app && this.app.ticker && this.viewport && this.grid);
  }

  startTicker(): void {
    if (this.app && this.app.ticker && !this.app.ticker.started) {
      logger.pixi.debug('Starting PIXI ticker manually');
      this.app.ticker.start();
    }
  }
}